import { ErrorHandler, Injectable } from '@angular/core'

import { DateService } from '@lemon/core/service/date.service'
import { StringService } from '@lemon/core/service/string.service'
import { ViitenumeroService } from '@lemon/core/service/viitenumero.service'

import { AsiakasIdPyynto, AsiakasIdVastaus, AsiakasYtunnusPyynto, AsiakasYtunnusVastaus } from '@lemon/lemonaid-lemonator-functions-and-lemonator-angular/model/kayttajahallinta'
import { KirjauksienLuontityyppi } from '../../../_jaettu-lemonator/model/kirjanpito'

import {
  AlvIlmoitusjakso,
  Asiakas,
  AsiakasListausAsiakkaanPaivitysTyoData,
  AsiakasListausHintojenPaivitysTyoData,
  AsiakasListausTilikausienPaivitysTyoData,
  Asiakastiedot,
  AsiakkaanKuukausihinta,
  AsiakkaanMaksutapa,
  AsiakkaanMaksutapaHistoria,
  AsiakkaanVientiLemonatoristaLemonaidiinTyo,
  AsiakkaanVientiPostgresiinTyo,
  EiYhtiomuotoaYhtiomuoto,
  Kayttaja,
  KayttajanVientiLemonatoristaLemonaidiinTyo,
  ListausAsiakas,
  Tilikausi,
  TyokieltoHistoriaEntry,
  TyokieltoTapahtumaTyyppi,
} from '../../../_jaettu-lemonator/model/asiakas'
import { AsiakasJaettuService } from '../../../_jaettu-lemonator/service/asiakas-jaettu.service'

import { LaskuttavaAsiakasProvider } from '../../../_jaettu-angular/service/lasku/laskuttava-asiakas.service'
import { TimestampService } from '../../../_jaettu-angular/service/timestamp-service'

import { KirjanpitajanTiedot, KirjautunutKayttajaService } from '../kirjautunut-kayttaja.service'
import { KirjautunutTilintarkastajaService } from '../kirjautunut-tilintarkastaja.service'
import { AsiakasKopioija, EI_VIELA_NUMEROA_ASIAKASNUMERO } from './asiakas.kopioija'

import { Roolit, UserFeature, Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { Laskuasetukset } from '@lemon/core-firebase/model/lasku'

import { BankBalanceCalcAlgorithm, SahkoisenTiliotteenAlatyyppi } from '@lemon/core-firebase/model/tiliote'
import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { BicService } from '@lemon/core/service/bic.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { IFirestoreBatch } from 'app/_jaettu-angular/base-firebase.service'
import { KirjanpitoTilikaudenLukemienPaivittaminenTyojonotiedot } from 'app/_jaettu-lemonator/model/tyojono'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs'
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators'
import { KirjanpitajanSarake, KirjanpitajanSarakkeenTila, KirjanpitajanSarakkeidenArvotAsiakkaalle } from '../../../_jaettu-lemonator/model/kirjanpitaja'
import { AsiakkaanMaksutavat } from '../../../_jaettu-lemonator/service/asiakas-jaettu.service'
import { AsiakasUriService } from '../../../_jaettu-lemonator/service/asiakas-uri.service'
import { FirebaseLemonaid, FirebaseLemonator } from '../firebase-lemonator.service'

export interface LokalisoituAlvIlmoitusjakso {
  koodi: AlvIlmoitusjakso
  nimi: string
  lyhenne: string
}
export interface LokalisoituYritysmuoto {
  koodi: Yritysmuoto | EiYhtiomuotoaYhtiomuoto
  nimi: string
  lyhenne: string
}

export interface AsiakkaanAvainTiedot {
  avain: string
  asiakasId: number
}

@Injectable()
export class LaskuttavaAsiakasLemonatorProvider extends LaskuttavaAsiakasProvider {
  constructor(private _asiakasService: AsiakasService) {
    super()
  }

  public annaLaskuttavaAsiakasAvain(): Promise<string | null> {
    return Promise.resolve('jzATU474P4BRCkEANZ1H') // Aina LEMON TREE !!!
  }

  public onkoOssRekisterissa(): Observable<boolean> {
    return this._asiakasService.nykyinenAsiakasObservable.pipe(map(asiakas => !!asiakas?.ossRekisterissa))
  }

  // Lemonatorissa laskuttaja on aina Lemon Tree
  public onkoAlvRekisterissa(): Observable<boolean> {
    return of(true)
  }
}

@Injectable()
export class AsiakasService {
  private nykyinenAsiakasAvainSubject = new BehaviorSubject<AsiakkaanAvainTiedot>(null)
  nykyinenAsiakasAvainObservable = this.nykyinenAsiakasAvainSubject.asObservable()
  nykyinenAsiakasObservable = this.nykyinenAsiakasAvainObservable.pipe(
    switchMap(asiakas => {
      if (asiakas) {
        return this.annaAsiakasObservable(asiakas.avain)
      }
      return of<Asiakas>(null)
    }),
    lemonShare(),
  )

  nykyisenAsiakkaanLaskuasetuksetObservable = this.nykyinenAsiakasAvainObservable.pipe(
    switchMap(asiakas => {
      if (asiakas) {
        const asetuksetUri = this._laskuUriService.getLaskuasetuksetUri(asiakas.avain, asiakas.avain)
        return this._firebaseLemonaid.firestoreDoc<Laskuasetukset>(asetuksetUri).listen()
      }
      return of(null)
    }),
    lemonShare(),
  )

  nykyisenAsiakkaanTilikaudetObservable: Observable<Tilikausi[]> = this.nykyinenAsiakasObservable.pipe(
    map(a => (a && a.tilikaudet ? a.tilikaudet.sort((t1, t2) => t1.loppuu.year - t2.loppuu.year) : [])),
    distinctUntilChanged((prev, curr) => {
      if (prev.length !== curr.length) {
        return false
      }

      // slice creates a shallow copy of the array so that we dont fuck up
      // the order of the original arrays as a side effect only because of
      // this change detection method
      const prevSorted = prev.slice().sort((a, b) => {
        return this.dateService.localDateToDate(a.alkaa).getTime() - this.dateService.localDateToDate(b.alkaa).getTime()
      })
      const currSorted = curr.slice().sort((a, b) => {
        return this.dateService.localDateToDate(a.alkaa).getTime() - this.dateService.localDateToDate(b.alkaa).getTime()
      })

      for (let i = 0; i < prevSorted.length; i++) {
        const p = prevSorted[i]
        const c = currSorted[i]
        if (this.dateService.paiviaValissaPaikallinen(p.alkaa, c.alkaa) !== 0) {
          return false
        }
        if (this.dateService.paiviaValissaPaikallinen(p.loppuu, c.loppuu) !== 0) {
          return false
        }
        if (p.avain !== c.avain) {
          return false
        }
        if (p.tilinpaatosStarted !== c.tilinpaatosStarted) {
          return false
        }
        if (p.tilinpaatoksenTuloslaskelma !== c.tilinpaatoksenTuloslaskelma) {
          return false
        }
        if (p.veroilmoitusSent !== c.veroilmoitusSent) {
          return false
        }
        if (p.veroilmoitusStarted !== c.veroilmoitusStarted) {
          return false
        }
        if ((p.tilikartanProfiili || 'maksuperusteinen') !== (c.tilikartanProfiili || 'maksuperusteinen')) {
          return false
        }
      }

      return true
    }),
    lemonShare(),
  )

  nykyisenAsiakkaanKayttajatObservable: Observable<Kayttaja[]> = this.nykyinenAsiakasAvainObservable.pipe(
    switchMap(asiakas => {
      // console.log('Asiakas emit 1')
      if (asiakas) {
        return this._firebase.firestoreCollection<Kayttaja>('asiakkaat/' + asiakas.avain + '/kayttajat').listen()
        // .pipe(tap(() => {  console.log('Firestore emit 1') }))
      }
      return of<Kayttaja[]>(null as Kayttaja[])
    }),
    lemonShare(),
    // tap(() => console.log('lemonShare 1'))
  )
  nykyisenAsiakkaanMaksutavatObservable: Observable<AsiakkaanMaksutapa[]> = this.nykyinenAsiakasAvainObservable.pipe(
    switchMap(asiakas => {
      // console.log('Asiakas emit 2')
      if (asiakas) {
        return this._firebase.firestoreCollection<AsiakkaanMaksutapa>('asiakkaat/' + asiakas.avain + '/maksutavat').listen()
        // .pipe(tap(() => { console.log('Firestore emit 2') }))
      }
      return of<AsiakkaanMaksutapa[]>(null as AsiakkaanMaksutapa[])
    }),
    lemonShare(),
    // tap(() => console.log('lemonShare 2'))
  )
  nykyisenAsiakkaanKirjanpidossaNaytettavatMaksutavatObservable: Observable<AsiakkaanMaksutapa[]> = this.nykyisenAsiakkaanMaksutavatObservable.pipe(
    map(mt => {
      if (!mt) {
        return []
      }
      const m: AsiakkaanMaksutapa[] = [AsiakkaanMaksutavat.MAKSUTAPA_MUU, ...mt, AsiakkaanMaksutavat.MAKSUTAPA_MYYNTITOSITE]
      return m
    }),
    lemonShare(),
  )
  nykyisenAsiakkaanKaikkiMaksutavatObservable: Observable<AsiakkaanMaksutapa[]> = this.nykyisenAsiakkaanMaksutavatObservable.pipe(
    map(mt => {
      if (!mt) {
        return []
      }
      return [
        AsiakkaanMaksutavat.MAKSUTAPA_MUU,
        AsiakkaanMaksutavat.MAKSUTAPA_MYYNTILASKU,
        AsiakkaanMaksutavat.MAKSUTAPA_MYYNTITOSITE,
        AsiakkaanMaksutavat.MAKSUTAPA_PALKKATOSITE,
        AsiakkaanMaksutavat.MAKSUTAPA_TILIOTETOSITE,
        AsiakkaanMaksutavat.MAKSUTAPA_SAHKOISET_LASKUT,
        ...mt,
        AsiakkaanMaksutavat.MAKSUTAPA_AUTOMAATTISET_JAKSOTUKSET,
      ]
    }),
    lemonShare(),
  )
  nykyisenAsiakkaanJaKayttajienRoolienPerusteellaAktivoitavatMaksutavatObservable: Observable<AsiakkaanMaksutapa[]> = combineLatest([
    this.nykyisenAsiakkaanMaksutavatObservable,
    this.nykyisenAsiakkaanKayttajatObservable,
  ]).pipe(
    map(([mt, kayttajat]) => {
      if (!mt) {
        return []
      }
      const maksutavat: AsiakkaanMaksutapa[] = [AsiakkaanMaksutavat.MAKSUTAPA_MUU].concat(mt)
      let naytaTiliotetositteet = false
      let naytaPalkkamateriaali = false
      if (kayttajat) {
        for (const kayttaja of kayttajat) {
          if (kayttaja.roolit) {
            if (kayttaja.roolit[Roolit.TOSITTEET_TILI]) {
              naytaTiliotetositteet = true
            }
            if (kayttaja.roolit[Roolit.TOSITTEET_PALKKA]) {
              naytaPalkkamateriaali = true
            }
          }
        }
      }

      maksutavat.push(AsiakkaanMaksutavat.MAKSUTAPA_MYYNTITOSITE)
      if (naytaTiliotetositteet) {
        maksutavat.push(AsiakkaanMaksutavat.MAKSUTAPA_TILIOTETOSITE)
      }
      if (naytaPalkkamateriaali) {
        maksutavat.push(AsiakkaanMaksutavat.MAKSUTAPA_PALKKATOSITE)
      }
      maksutavat.push(AsiakkaanMaksutavat.MAKSUTAPA_SAHKOISET_LASKUT)
      maksutavat.push(AsiakkaanMaksutavat.MAKSUTAPA_SAHKOISET_LASKUT_FAKE)

      return maksutavat
    }),
    lemonShare(),
  )
  nykyisenAsiakkaanJaKayttajienRoolienJaLaskunMaksutavatObservable: Observable<AsiakkaanMaksutapa[]> = this.nykyisenAsiakkaanJaKayttajienRoolienPerusteellaAktivoitavatMaksutavatObservable.pipe(
    map(maksutavat => {
      const mt = [].concat(maksutavat)
      mt.push(AsiakkaanMaksutavat.MAKSUTAPA_MYYNTILASKU)
      return mt
    }),
  )

  yritysmuodot: LokalisoituYritysmuoto[] = [
    { koodi: Yritysmuoto.AVOINYHTIO, nimi: 'Avoin yhtiö', lyhenne: 'Ay' },
    { koodi: Yritysmuoto.KOMMANDIITTIYHTIO, nimi: 'Kommandiittiyhtiö', lyhenne: 'Ky' },
    { koodi: Yritysmuoto.OSAKEYHTIO, nimi: 'Osakeyhtiö', lyhenne: 'Oy' },
    { koodi: Yritysmuoto.OSUUSKUNTA, nimi: 'Osuuskunta', lyhenne: 'Osk' },
    { koodi: Yritysmuoto.TOIMINIMI, nimi: 'Toiminimi', lyhenne: 'Tmi' },
    { koodi: Yritysmuoto.YHDISTYS, nimi: 'Yhdistys', lyhenne: 'Ry' },
  ]

  yritysmuodotJaEiTiedossa: LokalisoituYritysmuoto[] = [{ nimi: 'ei tiedossa', koodi: EiYhtiomuotoaYhtiomuoto.YHTIOMUOTO_EI_TIEDOSSA, lyhenne: 'ei tied.' }, ...this.yritysmuodot]

  alvIlmoitusjaksot: LokalisoituAlvIlmoitusjakso[] = [
    { koodi: AlvIlmoitusjakso.TUNTEMATON, nimi: 'Tuntematon', lyhenne: 'Tuntematon' },
    { koodi: AlvIlmoitusjakso.EI, nimi: 'Ei ALV-velvollinen', lyhenne: 'Ei ALV' },
    { koodi: AlvIlmoitusjakso.KK1, nimi: '1kk', lyhenne: '1kk (kuukausittain)' },
    { koodi: AlvIlmoitusjakso.KK3, nimi: '3kk', lyhenne: '3kk (neljännesvuosittain)' },
    { koodi: AlvIlmoitusjakso.KK12, nimi: '12kk', lyhenne: '12kk (vuosittain)' },
  ]

  constructor(
    private errorHandler: ErrorHandler,
    private _firebase: FirebaseLemonator,
    private _firebaseLemonaid: FirebaseLemonaid,
    private dateService: DateService,
    private timestampService: TimestampService,
    private kirjautunutKayttajaService: KirjautunutKayttajaService,
    private tilintarkastajaService: KirjautunutTilintarkastajaService,
    private viitenumeroService: ViitenumeroService,
    private stringService: StringService,
    private asiakasKopioija: AsiakasKopioija,
    private jaettuAsiakasService: AsiakasJaettuService,
    private _copyService: KopioijaPalvelu,
    private _asiakasUriService: AsiakasUriService,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _bicService: BicService,
    private _laskuUriService: LaskuUriService,
  ) {}

  public asetaNykyinenAsiakas(asiakas: Asiakas): void {
    const vanha = this.nykyinenAsiakasAvainSubject.value
    if (asiakas && !vanha) {
      this.nykyinenAsiakasAvainSubject.next({
        asiakasId: asiakas.asiakasId,
        avain: asiakas.avain,
      })
    } else if (vanha && !asiakas) {
      this.nykyinenAsiakasAvainSubject.next(null)
    } else if (vanha && asiakas && vanha.avain !== asiakas.avain) {
      this.nykyinenAsiakasAvainSubject.next({
        asiakasId: asiakas.asiakasId,
        avain: asiakas.avain,
      })
    }
  }

  public annaAsiakasObservable(avain: string): Observable<Asiakas> {
    return this._firebase.firestoreDoc<Asiakas>('asiakkaat/' + avain).listen()
  }

  public annaTyokieltoAsiakkaatObservable(): Observable<Asiakas[]> {
    return this._firebase.firestoreCollection<Asiakas>('asiakkaat').where('tyokielto', '==', true).listen()
  }

  public annaAsiakkaatYtunnuksella(ytunnus: string): Promise<AsiakasYtunnusVastaus> {
    const pyynto: AsiakasYtunnusPyynto = {
      ytunnus: ytunnus,
    }
    return this._firebase.functionsCall<AsiakasYtunnusPyynto, AsiakasYtunnusVastaus>('annaAsiakkaatYtunnuksella', pyynto)
  }

  public async paivitaAsiakas(
    asiakas: Asiakas,
    paivitaTilikausiinPerustuvatTiedot: 'paivita-tilikausiin-perustuvat-tiedot' | 'tilikaudet-eivat-voineet-muuttua',
    paivitaKuukausiruutujenHinnat: boolean,
    paivitaKayttajat: boolean,
    lahetaTyokieltoEmail?: boolean,
  ): Promise<void> {
    const kirjanpitajanTiedot = await this.kirjautunutKayttajaService.getKirjanpitajanTiedot()
    const batch = this._firebase.firestoreBatch()

    await this.lisaaAsiakasBatchiin(asiakas, kirjanpitajanTiedot, paivitaTilikausiinPerustuvatTiedot, paivitaKuukausiruutujenHinnat, paivitaKayttajat, batch, lahetaTyokieltoEmail)

    return batch.commit()
  }

  public async paivitaAsiakasJaTiedot(
    asiakas: Asiakas,
    tiedot: Asiakastiedot,
    paivitaTilikausiinPerustuvatTiedot: 'paivita-tilikausiin-perustuvat-tiedot' | 'tilikaudet-eivat-voineet-muuttua',
    paivitaKuukausiruutujenHinnat: boolean,
    paivitaKayttajat: boolean,
  ): Promise<void> {
    const kirjanpitajanTiedot = await this.kirjautunutKayttajaService.getKirjanpitajanTiedot()
    const batch = this._firebase.firestoreBatch()

    await this.lisaaAsiakasBatchiin(asiakas, kirjanpitajanTiedot, paivitaTilikausiinPerustuvatTiedot, paivitaKuukausiruutujenHinnat, paivitaKayttajat, batch)

    const tallennettava = this._copyService.cloneObjectDeep(tiedot)
    tallennettava.avain = tallennettava.avain ? tallennettava.avain : this._firebase.firestoreCreateId()
    tallennettava.asiakasAvain = asiakas.avain
    const tallennuksenAvain = this._firebase.firestoreCreateId()
    tallennettava.paivitetty = this.timestampService.now()
    tallennettava.paivittaja = kirjanpitajanTiedot.uid
    if (!tallennettava.luoja) {
      tallennettava.luoja = kirjanpitajanTiedot.uid
    }
    if (!tallennettava.luotu) {
      tallennettava.luotu = this.timestampService.now()
    }

    // Luo reffit
    const uri = this._asiakasUriService.annaAsiakastiedotUri(asiakas.avain)
    const historiaUri = this._asiakasUriService.annaAsiakastietojenHistoriaUri(asiakas.avain, tallennuksenAvain)

    // Aseta batchiin
    batch.set(uri, tallennettava, { merge: true })
    batch.set(historiaUri, tallennettava)

    return batch.commit()
  }

  // public async tallennaVastuuJaTuuraajaKirjanpitajat(muutokset: VastuuJaTuuraajaKirjanpitajaMuutokset): Promise<void> {

  //   const kirjanpitajanTiedot = await this.kirjautunutKayttajaService.getKirjanpitajanTiedot()
  //   const tallennuksenAvain = this._firebase.firestoreCreateId()
  //   let batch = this._firebase.firestoreBatch()
  //   let batchCount = 0

  //   for (const asiakasAvain of Object.keys(muutokset)) {
  //     const asiakkaanMuutokset = muutokset[asiakasAvain]

  //     const synkkaustyot = {}
  //     synkkaustyot[tallennuksenAvain] = true
  //     const synkkaustyonTiedot: AsiakkaanVientiLemonatoristaLemonaidiinTyo = { aloitettu: this.timestampService.now() }

  //     const updates = {}
  //     if (asiakkaanMuutokset.vastuu !== undefined) {
  //       updates['kasittelija'] = asiakkaanMuutokset.vastuu ? asiakkaanMuutokset.vastuu : null
  //     }
  //     if (asiakkaanMuutokset.tuuraaja !== undefined) {
  //       updates['kasittelijavara'] = asiakkaanMuutokset.tuuraaja ? asiakkaanMuutokset.tuuraaja : null
  //     }
  //     updates['synkkaustyot'] = synkkaustyot
  //     updates['paivitetty'] = new Date()
  //     updates['paivittaja'] = kirjanpitajanTiedot.uid

  //     const asiakasDoc = this._firebase.firestoreDoc('asiakkaat/' + asiakasAvain)
  //     const synkkaustyoRef = this._firebase.firestoreDoc('tyojono/' + asiakasAvain + '/asiakas-lemontree-export/' + tallennuksenAvain)
  //     batch.set(asiakasDoc, updates, { merge: true })
  //     batch.set(synkkaustyoRef, synkkaustyonTiedot)
  //     batchCount++
  //     batchCount++

  //     if (batchCount > 480) {
  //       await batch.commit()
  //       batch = this._firebase.firestoreBatch()
  //       batchCount = 0
  //     }
  //   }

  //   return batch.commit()
  // }

  public async tallennaMaksutavat(asiakas: Asiakas, maksutavat: AsiakkaanMaksutapa[], areAllAccessibleAccountsInMaksutavat?: boolean): Promise<void> {
    const kirjanpitajanTiedot = await this.kirjautunutKayttajaService.getKirjanpitajanTiedot()

    // Vie kaikki käyttäjät
    const kayttajat = await this._firebase.firestoreCollection<Kayttaja>('/asiakkaat/' + asiakas.avain + '/kayttajat').get()

    // Luo reffit
    const tallennuksenAvain = this._firebase.firestoreCreateId()
    const uri = '/asiakkaat/' + asiakas.avain
    const historiaUri = uri + '/historia/' + tallennuksenAvain
    const synkkaustyoUri = '/tyojono/' + asiakas.avain + '/asiakas-lemontree-export/' + tallennuksenAvain

    const synkkaustyot = {}
    synkkaustyot[tallennuksenAvain] = true
    const synkkaustyonTiedot: AsiakkaanVientiLemonatoristaLemonaidiinTyo = { aloitettu: this.timestampService.now() }

    const batch = this._firebase.firestoreBatch()

    for (const kayttaja of kayttajat) {
      const asiakastyonAvain = tallennuksenAvain + '_' + kayttaja.avain
      synkkaustyot[asiakastyonAvain] = true
      const asiakasSynkkaustyoUri = '/tyojono/' + asiakas.avain + '/kayttaja-lemontree-export/' + asiakastyonAvain
      const asiakasSynkkaustyonTiedot: KayttajanVientiLemonatoristaLemonaidiinTyo = { kayttajaAvain: kayttaja.avain, aloitettu: this.timestampService.now() }
      batch.set(asiakasSynkkaustyoUri, asiakasSynkkaustyonTiedot)
    }

    for (const maksutapa of maksutavat) {
      if (!maksutapa.avain) {
        maksutapa.avain = this._firebase.firestoreCreateId()
      }

      const bic = this._bicService.annaBicKoodi(maksutapa.sahkoisenTiliotteenTunniste)
      const isEnableBanking = maksutapa.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.ENABLE_BANKING
      const isAiiaWithSpecificBanks = maksutapa.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.AIIA && ['NDEAFIHH', 'ITELFIHH', 'POPFFI22', 'SBANFIHH', 'REVOLT21XXX'].includes(bic)
      const useVersionOneByDefault = isEnableBanking || isAiiaWithSpecificBanks

      if (useVersionOneByDefault) {
        // Hardcode to use VERSION_ONE
        maksutapa.bankBalanceCalcAlgorithm = BankBalanceCalcAlgorithm.VERSION_ONE
      }

      const maksutavatUri = uri + '/maksutavat/' + maksutapa.avain
      const maksutavatHistoriaUri = maksutavatUri + '/maksutavat-historia/' + this._firebase.firestoreCreateId()

      const maksutapaHistoria: AsiakkaanMaksutapaHistoria = Object.assign(
        {
          paivitetty: this.timestampService.now(),
          paivittaja: kirjanpitajanTiedot.uid,
        },
        maksutapa,
      )
      batch.set(maksutavatUri, maksutapa)
      batch.set(maksutavatHistoriaUri, maksutapaHistoria)
    }

    const asiakasUpdateData: Partial<Asiakas> = {
      paivitetty: this.timestampService.now(),
      paivittaja: kirjanpitajanTiedot.uid,
      synkkaustyot: synkkaustyot,
      areAllAccessibleAccountsInMaksutavat: areAllAccessibleAccountsInMaksutavat === undefined ? null : areAllAccessibleAccountsInMaksutavat,
    }

    batch.update(uri, asiakasUpdateData)

    batch.set(synkkaustyoUri, synkkaustyonTiedot)
    batch.set(historiaUri, this.asiakasKopioija.kopioiAsiakas(asiakas))

    return batch.commit()
  }

  public async lisaaTilikausi(asiakas: Asiakas, tilikausi: Tilikausi): Promise<void> {
    const kirjanpitajanTiedot = await this.kirjautunutKayttajaService.getKirjanpitajanTiedot()

    // Luo reffit
    const tallennuksenAvain = this._firebase.firestoreCreateId()
    const uri = '/asiakkaat/' + asiakas.avain
    const historiaUri = uri + '/historia/' + tallennuksenAvain
    const synkkaustyoUri = '/tyojono/' + asiakas.avain + '/asiakas-lemontree-export/' + tallennuksenAvain

    const synkkaustyot = asiakas.synkkaustyot ? asiakas.synkkaustyot : {}
    synkkaustyot[tallennuksenAvain] = true
    const synkkaustyonTiedot: AsiakkaanVientiLemonatoristaLemonaidiinTyo = { aloitettu: this.timestampService.now() }

    const batch = this._firebase.firestoreBatch()

    const tilikaudet = asiakas.tilikaudet ? [...asiakas.tilikaudet] : []
    tilikaudet.push(tilikausi)

    const objectToUpdate: Partial<Asiakas> = {
      tilikaudet: tilikaudet,
      paivitetty: this.timestampService.now(),
      paivittaja: kirjanpitajanTiedot.uid,
      synkkaustyot: synkkaustyot,
    }

    batch.update(uri, objectToUpdate)
    batch.set(synkkaustyoUri, synkkaustyonTiedot)
    batch.set(historiaUri, this.asiakasKopioija.kopioiAsiakas(asiakas))
    this.lisaaListausAsiakkaanPaivitysBatchiin(asiakas, batch)
    return batch.commit()
  }

  public async tallennaSarakkeenArvo(asiakas: ListausAsiakas, kirjanpitajaUid: string, sarake: KirjanpitajanSarake, tila: KirjanpitajanSarakkeenTila): Promise<void> {
    // NOTE! In case of holvi pool asiakkaat, these are saved under the holvi user, and not the current user.
    // So, in that case the uri in to which the doc is saved should differ from the uid who saved.

    // Luo reffit
    const tallennuksenAvain = this._firebase.firestoreCreateId()
    const uri = this._asiakasUriService.annaListausSarakkeenUri(kirjanpitajaUid, asiakas.k)
    const historiaUri = this._asiakasUriService.annaListausSarakkeenHistoriaUri(kirjanpitajaUid, asiakas.k, tallennuksenAvain)

    const kirjanpitajanTiedot = await this.kirjautunutKayttajaService.getKirjanpitajanTiedot()
    const arvotAsiakkaalle = await this._firebase
      .firestoreDoc<KirjanpitajanSarakkeidenArvotAsiakkaalle>(uri)
      .get()
      .then(arvot => {
        if (arvot) {
          return arvot
        }
        const newData: KirjanpitajanSarakkeidenArvotAsiakkaalle = {
          a: asiakas.k,
          p: asiakas.p,
          s: asiakas.s,
          v: {},
        }
        return newData
      })

    arvotAsiakkaalle.a = asiakas.k
    arvotAsiakkaalle.p = asiakas.p
    if (asiakas.s) {
      arvotAsiakkaalle.s = asiakas.s
    } else {
      delete arvotAsiakkaalle.s
    }

    const tilanAvain = tila && !tila.oletus ? tila.avain : null
    arvotAsiakkaalle.v[sarake.avain] = { p: kirjanpitajanTiedot.uid, v: tilanAvain, u: this.dateService.dateToNumberTimestamp(new Date()) }

    const batch = this._firebase.firestoreBatch()
    batch.set(uri, arvotAsiakkaalle)
    batch.set(historiaUri, arvotAsiakkaalle)
    return batch.commit()
  }

  public async tallennaMuistiinpano(asiakas: Asiakas, muistiinpanot: string, vari: string): Promise<void> {
    const kirjanpitajanTiedot = await this.kirjautunutKayttajaService.getKirjanpitajanTiedot()

    // Luo reffit
    const tallennuksenAvain = this._firebase.firestoreCreateId()
    const uri = '/asiakkaat/' + asiakas.avain
    const historiaUri = uri + '/historia/' + tallennuksenAvain

    const batch = this._firebase.firestoreBatch()
    batch.update(uri, {
      muistiinpanot: muistiinpanot,
      muistiinpanojenVari: vari,
      paivitetty: this.timestampService.now(),
      paivittaja: kirjanpitajanTiedot.uid,
    })
    batch.set(historiaUri, this.asiakasKopioija.kopioiAsiakas(asiakas))
    this.lisaaListausAsiakkaanPaivitysBatchiin(asiakas, batch)
    return batch.commit()
  }

  public async tallennaKirjanpidonMuistiinpanonAukiolo(asiakas: Asiakas, auki: boolean): Promise<void> {
    const kirjanpitajanTiedot = await this.kirjautunutKayttajaService.getKirjanpitajanTiedot()

    // Luo reffit
    const tallennuksenAvain = this._firebase.firestoreCreateId()
    const uri = '/asiakkaat/' + asiakas.avain
    const historiaUri = uri + '/historia/' + tallennuksenAvain

    const update: Partial<Asiakas> = {
      paivitetty: this.timestampService.now(),
      paivittaja: kirjanpitajanTiedot.uid,
    }

    if (auki) {
      update.kirjanpidonMuistiinpanoAuki = true
    } else {
      update.kirjanpidonMuistiinpanoAuki = this._firebase.firestoreDeleteMarker()
    }

    const batch = this._firebase.firestoreBatch()
    batch.update(uri, update)
    batch.set(historiaUri, this.asiakasKopioija.kopioiAsiakas(asiakas))
    return batch.commit()
  }

  public async tallennaKirjanpidonMuistiinpano(asiakas: Asiakas, muistiinpanot: string): Promise<void> {
    const kirjanpitajanTiedot = await this.kirjautunutKayttajaService.getKirjanpitajanTiedot()

    // Luo reffit
    const tallennuksenAvain = this._firebase.firestoreCreateId()
    const uri = '/asiakkaat/' + asiakas.avain
    const historiaUri = uri + '/historia/' + tallennuksenAvain

    const update: Partial<Asiakas> = {
      kirjanpidonMuistiinpanot: muistiinpanot,
      paivitetty: this.timestampService.now(),
      paivittaja: kirjanpitajanTiedot.uid,
    }

    const batch = this._firebase.firestoreBatch()
    batch.update(uri, update)
    batch.set(historiaUri, this.asiakasKopioija.kopioiAsiakas(asiakas))
    return batch.commit()
  }

  public lisaaAsiakkaanHistoriaBatchiin(asiakas: Asiakas, batch: IFirestoreBatch) {
    // Luo reffit
    const tallennuksenAvain = this._firebase.firestoreCreateId()
    const uri = '/asiakkaat/' + asiakas.avain
    const historiaUri = uri + '/historia/' + tallennuksenAvain
    batch.set(historiaUri, this.asiakasKopioija.kopioiAsiakas(asiakas))
  }

  public async lisaaTyokieltoEmailLahetysBatchiin(asiakas: Asiakas, batch: IFirestoreBatch) {
    if (asiakas.tyokielto === undefined || asiakas.tyokielto === null) {
      console.error(`Asiakkaalla ${asiakas.avain} ei ole työkielto-tietoa, joten työkielto-emailia ei voida lähettää`)
      return
    }
    const tyojonoId: string = this._firebase.firestoreCreateId()
    const tyojonoData = {
      luotu: this.timestampService.now(),
      asiakasAvain: asiakas.avain,
    }
    const tyojonoUri = asiakas.tyokielto
      ? this._asiakasUriService.annaAsetaTyokieltoEmailLahetysTyoUri(tyojonoData.asiakasAvain, tyojonoId)
      : this._asiakasUriService.annaLopetaTyokieltoEmailLahetysTyoUri(tyojonoData.asiakasAvain, tyojonoId)
    batch.set(tyojonoUri, tyojonoData)
  }

  public lisaaTyokieltoHistoriaBatchiin(asiakas: Asiakas, tyyppi: TyokieltoTapahtumaTyyppi, paivittaja: string, batch: IFirestoreBatch): void {
    const historiaAvain = this._firebase.firestoreCreateId()
    const uri = this._asiakasUriService.annaTyokieltoHistoriaUri(asiakas.avain, historiaAvain)
    const entry: TyokieltoHistoriaEntry = {
      avain: historiaAvain,
      tyyppi,
      paivitetty: this.timestampService.now(),
      paivittaja,
    }
    batch.set(uri, entry)
  }

  private _annaDefaultMaksutavat(): AsiakkaanMaksutapa[] {
    const oletustavat: AsiakkaanMaksutapa[] = []

    oletustavat.push({
      avain: this._firebase.firestoreCreateId(),
      aktiivinen: true,
      base64Kuva:
        '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',
      nimi: 'Yrityksen tililtä',
      tunniste: 1,
      kirjauksienLuontityyppi: KirjauksienLuontityyppi.SAHKOINEN_TILIOTE,
      oletusVastatili: null,
      sahkoisenTiliotteenTunniste: null,
      tilitapahtumatSynkronoidaanAlkaen: null,
      tilitapahtumatSynkronoidaanLoppuen: null,
    })

    oletustavat.push({
      avain: this._firebase.firestoreCreateId(),
      aktiivinen: true,
      base64Kuva:
        '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',
      nimi: 'Omalla rahalla',
      tunniste: 2,
      kirjauksienLuontityyppi: KirjauksienLuontityyppi.TOSITTEISTA_GENEROITAVA_TILIOTE,
      oletusVastatili: null,
      sahkoisenTiliotteenTunniste: null,
      tilitapahtumatSynkronoidaanAlkaen: null,
      tilitapahtumatSynkronoidaanLoppuen: null,
      holviOmallaRahallaAccount: true,
    })

    return oletustavat
  }

  public async lisaaAsiakasBatchiin(
    asiakas: Asiakas,
    kirjanpitajanTiedot: KirjanpitajanTiedot,
    paivitaTilikausiinPerustuvatTiedot: 'paivita-tilikausiin-perustuvat-tiedot' | 'tilikaudet-eivat-voineet-muuttua',
    paivitaKuukausiruutujenHinnat: boolean,
    paivitaKayttajat: boolean,
    batch: IFirestoreBatch,
    lahetaTyokieltoEmail?: boolean,
  ): Promise<void> {
    // Aseta avain
    asiakas.avain = asiakas.avain ? asiakas.avain : this._firebase.firestoreCreateId()
    const tallennuksenAvain = this._firebase.firestoreCreateId()

    if (asiakas.asiakasId === EI_VIELA_NUMEROA_ASIAKASNUMERO) {
      const pyynto: AsiakasIdPyynto = {
        asiakasAvain: asiakas.avain,
      }
      const vastaus = await this._firebase.functionsCall<AsiakasIdPyynto, AsiakasIdVastaus>('annaAsiakkaanId', pyynto)
      asiakas.asiakasId = vastaus.asiakasId
    }

    // Aseta muut tiedot
    asiakas.paivitetty = this.timestampService.now()
    asiakas.paivittaja = kirjanpitajanTiedot.uid
    if (!asiakas.luoja) {
      asiakas.luoja = kirjanpitajanTiedot.uid
    }
    if (!asiakas.luotu) {
      asiakas.luotu = this.timestampService.now()
    }

    // Jos ei ole viitenumeroa, generoi sellainen
    if (!asiakas.viitenumero) {
      const pohjaluku = '99' + this.stringService.leftpad(asiakas.asiakasId + '', 3, '0')
      const viitenumero = this.viitenumeroService.luoViitenumero(pohjaluku)
      asiakas.viitenumero = this.viitenumeroService.muotoileViitenumero(viitenumero)
    }

    // Muotoile viitenumero tallentamista varten
    const muotoiltu = this.viitenumeroService.muotoileViitenumero(asiakas.viitenumero)
    const whitespacePoistettu = this.stringService.removeAllWhiteSpaces(muotoiltu)
    if (whitespacePoistettu) {
      asiakas.viitenumero = whitespacePoistettu
    } else {
      throw new Error('Viitenumero ' + asiakas.viitenumero + ' katosi asiakkaalta ' + asiakas.avain + '!')
    }

    const nykyinenKirjanpitaja = this.jaettuAsiakasService.annaNykyinenVastuukirjanpitaja(asiakas)
    asiakas.kasittelija = nykyinenKirjanpitaja ? nykyinenKirjanpitaja.kirjanpitajanAvain : null

    const nykyinenVarakirjanpitaja = this.jaettuAsiakasService.annaNykyinenVarakirjanpitaja(asiakas)
    asiakas.kasittelijavara = nykyinenVarakirjanpitaja ? nykyinenVarakirjanpitaja.kirjanpitajanAvain : null

    // Luo synkkaustyö tallennukselle
    asiakas.synkkaustyot = {}
    asiakas.synkkaustyot[tallennuksenAvain] = true
    const synkkaustyonTiedot: AsiakkaanVientiLemonatoristaLemonaidiinTyo = { aloitettu: this.timestampService.now() }

    // Luo reffit
    const uri = 'asiakkaat/' + asiakas.avain
    const historiaUri = uri + '/historia/' + tallennuksenAvain
    const synkkaustyoUri = 'tyojono/' + asiakas.avain + '/asiakas-lemontree-export/' + tallennuksenAvain

    if (paivitaKayttajat) {
      // Vie kaikki käyttäjät
      const kayttajat = await this._firebase.firestoreCollection<Kayttaja>('/asiakkaat/' + asiakas.avain + '/kayttajat').get()
      for (const kayttaja of kayttajat) {
        const asiakastyonAvain = tallennuksenAvain + '_' + kayttaja.avain
        asiakas.synkkaustyot[asiakastyonAvain] = true
        const asiakasSynkkaustyoUri = '/tyojono/' + asiakas.avain + '/kayttaja-lemontree-export/' + asiakastyonAvain
        const asiakasSynkkaustyonTiedot: KayttajanVientiLemonatoristaLemonaidiinTyo = { kayttajaAvain: kayttaja.avain, aloitettu: this.timestampService.now() }
        batch.set<KayttajanVientiLemonatoristaLemonaidiinTyo>(asiakasSynkkaustyoUri, asiakasSynkkaustyonTiedot)
      }
    }

    const kopioitu = this.asiakasKopioija.kopioiAsiakas(asiakas)
    const kopioituHistoria = this.asiakasKopioija.kopioiAsiakas(asiakas)

    if (!kopioitu.ossRekisterissa) {
      kopioitu.ossRekisterissa = this._firebase.firestoreDeleteMarker()
    }

    if (!kopioitu.kasittelePalkatAlvittomana) {
      kopioitu.kasittelePalkatAlvittomana = this._firebase.firestoreDeleteMarker()
    }

    if (!kopioitu.testiasiakas) {
      kopioitu.testiasiakas = this._firebase.firestoreDeleteMarker()
    }

    if (!kopioitu.tilinpaatosRekisteroidaanBruttotuloslaskelmalla) {
      kopioitu.tilinpaatosRekisteroidaanBruttotuloslaskelmalla = this._firebase.firestoreDeleteMarker()
    }

    if (!kopioitu.estaAlvVerkkolaskujenLahetys) {
      kopioitu.estaAlvVerkkolaskujenLahetys = this._firebase.firestoreDeleteMarker()
    }

    if (!kopioitu.kirjanpitoviestienLisavastaanottajat || kopioitu.kirjanpitoviestienLisavastaanottajat.length < 1) {
      kopioitu.kirjanpitoviestienLisavastaanottajat = this._firebase.firestoreDeleteMarker()
    }

    if (!kopioitu.tyokieltoAlku) {
      kopioitu.tyokieltoAlku = this._firebase.firestoreDeleteMarker()
    }

    // Aseta batchiin
    batch.set(uri, kopioitu, { merge: true })
    batch.set(historiaUri, kopioituHistoria)
    batch.set(synkkaustyoUri, synkkaustyonTiedot)
    this.lisaaAsiakkaanPostgresVientiBatchiin(asiakas, tallennuksenAvain, batch)
    this.lisaaListausAsiakkaanPaivitysBatchiin(asiakas, batch)
    if (lahetaTyokieltoEmail) {
      this.lisaaTyokieltoEmailLahetysBatchiin(asiakas, batch)
      this.lisaaTyokieltoHistoriaBatchiin(asiakas, asiakas.tyokielto ? 'asetettu' : 'poistettu-manuaalisesti', kirjanpitajanTiedot.uid, batch)
    }

    if (paivitaTilikausiinPerustuvatTiedot === 'paivita-tilikausiin-perustuvat-tiedot') {
      this._lisaaListausRuutujenTilikausiPaivitysBatchiin(asiakas, batch)
      this._lisaaTilikausienSummienPaivitysBatchiin(asiakas, batch)
    }

    if (paivitaKuukausiruutujenHinnat) {
      this._lisaaListausRuutujenHintaPaivitysBatchiin(asiakas, batch)
    }
  }

  private _lisaaTilikausienSummienPaivitysBatchiin(asiakas: Asiakas, batch: IFirestoreBatch) {
    if (!asiakas?.tilikaudet?.length) {
      return
    }
    const tilikausi = asiakas.tilikaudet.reduce((a, b) => (this.dateService.compareLocalDates(a.loppuu, '<', b.loppuu) ? a : b))
    if (!tilikausi.avain) {
      return
    }
    const tyojonoAvain = this._firebase.firestoreCreateId()
    const uri = this._kirjanpitoUriService.annaTyojonoTilikaudenLukujenPaivitysUri(asiakas.avain, tilikausi.avain + tyojonoAvain)
    const tyodata: KirjanpitoTilikaudenLukemienPaivittaminenTyojonotiedot = {
      asiakasAvain: asiakas.avain,
      jonoonTallennettuAika: this._firebase.firestoreServerTimestamp(),
      tilikausiAvain: tilikausi.avain,
      laskeAlvIlmoitusKuukaudelle1: null,
      laskeAlvIlmoitusKuukaudelle2: null,
      paivitaSeuraavantilikaudenPoistokirjauksetTilille: 'paivita-kaikki',
    }
    batch.set(uri, tyodata)
  }

  private _lisaaListausRuutujenTilikausiPaivitysBatchiin(asiakas: Asiakas, batch: IFirestoreBatch) {
    const uri = this._asiakasUriService.annaListausTilikausienPaivitysTyoUri(asiakas.avain, this._firebase.firestoreCreateId())
    const tyodata: AsiakasListausTilikausienPaivitysTyoData = {
      asiakasAvain: asiakas.avain,
      aloitettu: this.timestampService.now(),
    }
    batch.set(uri, tyodata)
  }

  private _lisaaListausRuutujenHintaPaivitysBatchiin(asiakas: Asiakas, batch: IFirestoreBatch) {
    const uri = this._asiakasUriService.annaListausHintojenPaivitysTyoUri(asiakas.avain, this._firebase.firestoreCreateId())
    const tyodata: AsiakasListausHintojenPaivitysTyoData = {
      asiakasAvain: asiakas.avain,
      aloitettu: this.timestampService.now(),
    }
    batch.set(uri, tyodata)
  }

  public lisaaAsiakkaanPostgresVientiBatchiin(asiakas: Asiakas, tallennuksenAvain: string, batch: IFirestoreBatch) {
    if (!asiakas.tagit) {
      return
    }
    const uri = this._asiakasUriService.annaAsiakkaanPostgresVientiTyoUri(asiakas.avain, tallennuksenAvain)
    const tyodata: AsiakkaanVientiPostgresiinTyo = {
      aloitettu: this.timestampService.now(),
    }
    batch.set<AsiakkaanVientiPostgresiinTyo>(uri, tyodata)
  }

  public lisaaListausAsiakkaanPaivitysBatchiin(asiakas: Asiakas, batch: IFirestoreBatch) {
    const uri = this._asiakasUriService.annaListausAsiakkaanPaivitysTyoUri(asiakas.avain, this._firebase.firestoreCreateId())
    const tyodata: AsiakasListausAsiakkaanPaivitysTyoData = {
      asiakasAvain: asiakas.avain,
      aloitettu: this.timestampService.now(),
    }
    batch.set(uri, tyodata)
  }

  public annaNykyinenJaViimeisinHinta(asiakas: Pick<Asiakas, 'hinnat'>): { voimassaoleva: AsiakkaanKuukausihinta | null; viimeinenTulevaisuudessa: AsiakkaanKuukausihinta | null } {
    const paluuarvo = {
      voimassaoleva: this.jaettuAsiakasService.annaNykyinenHintaObjekti(asiakas),
      viimeinenTulevaisuudessa: this.annaViimeinenHinta(asiakas),
    }
    return paluuarvo
  }

  public annaViimeinenHinta(asiakas: Pick<Asiakas, 'hinnat'>): AsiakkaanKuukausihinta | null {
    if (asiakas && asiakas.hinnat) {
      // if (asiakas.asiakasId === 9) { console.log(asiakas.hinnat) }
      const kuukausi = this.dateService.currentLocalMonth()
      let loytynytHinta: AsiakkaanKuukausihinta = null
      for (const hinta of asiakas.hinnat) {
        if (this.dateService.compareLocalMonths(hinta.voimassaAlkaen, '<=', kuukausi)) {
          if (loytynytHinta === null) {
            // console.log('Valittiin', hinta)
            loytynytHinta = hinta
          } else {
            if (this.dateService.compareLocalDates(hinta.voimassaAlkaen, '>', loytynytHinta.voimassaAlkaen)) {
              // console.log('Valittiin', hinta, 'vanha', loytynytHinta)
              loytynytHinta = hinta
            }
          }
        }
      }
      return loytynytHinta
    }
    return null
  }

  lisaaDefaultMaksutavatAsiakkaalle(asiakas: Asiakas): Promise<any> {
    const batch = this._firebase.firestoreBatch()
    for (const maksutapa of this._annaDefaultMaksutavat()) {
      if (!maksutapa.avain) {
        maksutapa.avain = this._firebase.firestoreCreateId()
      }
      const maksutavatUri = 'asiakkaat/' + asiakas.avain + '/maksutavat/' + maksutapa.avain
      batch.set(maksutavatUri, maksutapa)
    }
    return batch.commit()
  }

  readonly KAYTTAJA_ROOLI_DISPLAY_NAMES = new Map<Roolit, string>([
    [Roolit.LASKUTUS, 'Laskutus'],
    [Roolit.TOSITTEET_OSTO, 'Ostotositteet'],
    [Roolit.TOSITTEET_MYYNTI, 'Myyntitositteet'],
    [Roolit.TOSITTEET_TILI, 'Tiliotteet'],
    [Roolit.TOSITTEET_PALKKA, 'Palkkamateriaali'],
    [Roolit.SAHKOISTEN_LASKUJEN_VASTAANOTTO, 'Sähköisten laskujen vastaanotto'],
    [Roolit.PALKAT, 'Palkat'],
    [Roolit.MAKSUT, 'Ostolaskut'],
    [Roolit.MAKSUT_VASTAANOTTAJA, 'Ostolaskujen käsittelijä'],
    [Roolit.MAKSUT_HYVAKSYJA, 'Ostolaskujen hyväksyjä'],
    [Roolit.MAKSUT_MAKSAJA, 'Ostolaskujen maksaja'],
    [Roolit.RAPORTIT, 'Raportit'],
    [Roolit.HALLINTO_SOPIMUKSEN_HYVAKSYJA, 'Sopimuksen hyväksyjä'],
    [Roolit.HALLINTO_YHTEYSHENKILO, 'Yhteyshenkilö'],
    [Roolit.HALLINTO_VIIA_VALTUUTUKSEN_ANTAJA, 'Pankkiyhteysvaltuutuksen antaja (PSD)'],
    [Roolit.HALLINTO_TUNTEMISTIETOJEN_ANTAJA, 'Tuntemistietojen antaja'],
    [Roolit.TUNNISTETAAN, 'Tunnistetaan'],
  ])

  getKayttajaRooliDisplayName(rooliKey: Roolit): string {
    return this.KAYTTAJA_ROOLI_DISPLAY_NAMES.get(rooliKey) || rooliKey
  }

  readonly KAYTTAJA_FEATURE_DISPLAY_NAMES = new Map<UserFeature, string>([[UserFeature.CAN_EDIT_PENALTY_INTEREST, 'Voi muokata laskujen viivästyskorkoa']])

  getKayttajaFeatureDisplayName(featureKey: UserFeature): string {
    return this.KAYTTAJA_FEATURE_DISPLAY_NAMES.get(featureKey) || featureKey
  }
}
